import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, MapPin, Loader2, AlertCircle, ImagePlus, X } from "lucide-react";
import { type BusinessKind } from "@/lib/admin-labels";
import { LANGS, LanguageSwitcher, tKind, useT } from "@/lib/i18n";
import { LocationPreviewMap } from "@/components/LocationPreviewMap";

import { REG_META, pickMeta } from "@/lib/page-meta";

export const Route = createFileRoute("/registrace-podniku")({
  ssr: false,
  component: RegistracePodniku,
  validateSearch: (search: Record<string, unknown>): { lang?: string } => ({
    lang: typeof search.lang === "string" ? search.lang : undefined,
  }),
  head: ({ match }) => {
    const m = pickMeta(REG_META, (match.search as { lang?: string }).lang);
    const url = "https://cerveza.international/registrace-podniku";
    return {
      meta: [
        { title: m.title },
        { name: "description", content: m.description },
        { property: "og:title", content: m.title },
        { property: "og:description", content: m.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

const KIND_OPTIONS: BusinessKind[] = [
  "restaurace", "bar", "kavarna", "cajovna", "stanek", "jidelna", "mlecny_bar", "food_truck", "rychle_obcerstveni", "bistro",
];

// Podporované země pro auto-doplnění PSČ → obec, region, souřadnice.
// CZ: lokální DB (postal_codes). Ostatní: veřejné API zippopotam.us.
const COUNTRY_CODES: string[] = [
  "CZ", "SK", "PL", "DE", "AT", "CH", "FR", "ES", "IT", "PT",
  "NL", "BE", "GB", "US", "CA", "MX", "AU",
];

const POSTAL_FORMAT: Record<string, RegExp> = {
  CZ: /^\d{3}\s?\d{2}$/,
  SK: /^\d{3}\s?\d{2}$/,
  PL: /^\d{2}-\d{3}$/,
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
  FR: /^\d{5}$/,
  ES: /^\d{5}$/,
  IT: /^\d{5}$/,
  PT: /^\d{4}-\d{3}$/,
  NL: /^\d{4}\s?[A-Za-z]{2}$/,
  BE: /^\d{4}$/,
  GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/,
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/,
  MX: /^\d{5}$/,
  AU: /^\d{4}$/,
};

const makeSchema = (t: (k: string) => string) => z.object({
  name: z.string().trim().min(2, t("reg.name")).max(120),
  kind: z.enum([
    "restaurace", "bar", "kavarna", "cajovna", "stanek", "jidelna", "mlecny_bar", "food_truck", "rychle_obcerstveni", "bistro",
  ]),
  country: z.string().length(2),
  postal_code: z.string().trim().min(3).max(12),
  city: z.string().trim().min(1).max(120),
  street: z.string().trim().max(200).optional().or(z.literal("")),
  description: z.string().trim().max(1000).optional().or(z.literal("")),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

type PostalRow = {
  postal_code: string;
  place_name: string;
  admin_name1: string | null;
  admin_name2: string | null;
  latitude: number | null;
  longitude: number | null;
};

function normalizePostal(country: string, raw: string) {
  const trimmed = raw.trim().replace(/\s+/g, " ");
  if (country === "CZ" || country === "SK") {
    const digits = trimmed.replace(/\s/g, "");
    if (/^\d{5}$/.test(digits)) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  }
  if (country === "PL") {
    const digits = trimmed.replace(/[-\s]/g, "");
    if (/^\d{5}$/.test(digits)) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }
  if (country === "PT") {
    const digits = trimmed.replace(/[-\s]/g, "");
    if (/^\d{7}$/.test(digits)) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }
  if (country === "NL") {
    const m = trimmed.replace(/\s+/g, "").match(/^(\d{4})([A-Za-z]{2})$/);
    if (m) return `${m[1]} ${m[2].toUpperCase()}`;
  }
  if (country === "GB" || country === "CA") return trimmed.toUpperCase();
  return trimmed;
}

// Veřejné API zippopotam.us — vrací obec, region (state) a souřadnice
// pro PSČ desítek zemí. Používá se jako fallback pro vše mimo CZ.
async function fetchZippopotam(country: string, postal: string): Promise<PostalRow[]> {
  const url = `https://api.zippopotam.us/${country.toLowerCase()}/${encodeURIComponent(postal)}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const json = (await res.json()) as {
    "post code"?: string;
    places?: Array<{
      "place name": string;
      state?: string;
      "state abbreviation"?: string;
      latitude: string;
      longitude: string;
    }>;
  };
  return (json.places ?? []).map((p) => ({
    postal_code: json["post code"] ?? postal,
    place_name: p["place name"],
    admin_name1: p.state || null,
    admin_name2: p["state abbreviation"] || null,
    latitude: p.latitude ? Number(p.latitude) : null,
    longitude: p.longitude ? Number(p.longitude) : null,
  }));
}

function RegistracePodniku() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useT();

  const [ownerConfirmed, setOwnerConfirmed] = useState(false);
  const [confirmedKinds, setConfirmedKinds] = useState<BusinessKind[]>([]);
  const [gatePassed, setGatePassed] = useState(false);
  const [country, setCountry] = useState("CZ");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<BusinessKind | "">("");
  const [postalInput, setPostalInput] = useState("");
  const [selected, setSelected] = useState<PostalRow | null>(null);
  const [coord, setCoord] = useState<{ lat: number; lng: number } | null>(null);
  const [street, setStreet] = useState("");
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  const toggleConfirmedKind = (k: BusinessKind) => {
    setConfirmedKinds((prev) => {
      if (prev.includes(k)) return prev.filter((x) => x !== k);
      if (prev.length >= 2) {
        toast.error(t("reg.maxTwoKinds"));
        return prev;
      }
      return [...prev, k];
    });
  };

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const accepted: File[] = [];
    const urls: string[] = [];
    Array.from(files).forEach((f) => {
      if (!f.type.startsWith("image/")) {
        toast.error(t("photos.invalidType"));
        return;
      }
      if (f.size > 3 * 1024 * 1024) {
        toast.error(t("photos.tooLarge"));
        return;
      }
      accepted.push(f);
      urls.push(URL.createObjectURL(f));
    });
    if (accepted.length) {
      setPhotos((p) => [...p, ...accepted]);
      setPreviews((p) => [...p, ...urls]);
    }
  };

  const removePhoto = (i: number) => {
    setPhotos((p) => p.filter((_, idx) => idx !== i));
    setPreviews((p) => {
      const url = p[i];
      if (url) URL.revokeObjectURL(url);
      return p.filter((_, idx) => idx !== i);
    });
  };

  useEffect(() => () => { previews.forEach((u) => URL.revokeObjectURL(u)); }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth", search: { redirect: "/registrace-podniku" } });
      return;
    }
  
  }, [authLoading, user, navigate]);

  const postal = normalizePostal(country, postalInput);
  const postalValid = POSTAL_FORMAT[country]?.test(postal) ?? postal.length >= 3;

  // Dotaz na PSČ – CZ → lokální DB, ostatní země → zippopotam.us
  const { data: matches = [], isFetching: psfetching } = useQuery({
    queryKey: ["postal", country, postal],
    enabled: postalValid && postal.length >= 3,
    queryFn: async () => {
      if (country === "CZ") {
        const { data, error } = await supabase
          .from("postal_codes")
          .select("postal_code, place_name, admin_name1, admin_name2, latitude, longitude")
          .eq("country_code", country)
          .eq("postal_code", postal)
          .order("place_name", { ascending: true })
          .limit(50);
        if (error) throw error;
        return (data ?? []) as PostalRow[];
      }
      return await fetchZippopotam(country, postal);
    },
  });

  // Reset výběru když uživatel změní PSČ
  useEffect(() => {
    if (selected && selected.postal_code !== postal) { setSelected(null); setCoord(null); }
  }, [postal, selected]);

  // Když uživatel vybere obec, předvyplň souřadnice (lze upravit na mapě).
  useEffect(() => {
    if (selected && selected.latitude != null && selected.longitude != null) {
      setCoord({ lat: selected.latitude, lng: selected.longitude });
    }
  }, [selected]);

  const addressOk = !!selected && coord != null;

  const createBusiness = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error(t("reg.notSignedIn"));
      if (!selected || !coord) throw new Error(t("reg.pickCity"));
      // Build cleaned per-language descriptions map (drop empty entries, trim, cap length).
      const cleanedDescriptions: Record<string, string> = {};
      for (const { code } of LANGS) {
        const v = (descriptions[code] ?? "").trim().slice(0, 1000);
        if (v) cleanedDescriptions[code] = v;
      }
      // Primary description: prefer cs, then current lang, then any.
      const primaryDescription =
        cleanedDescriptions.cs ??
        cleanedDescriptions[lang] ??
        Object.values(cleanedDescriptions)[0] ??
        "";
      const payload = makeSchema(t).parse({
        name,
        kind,
        country,
        postal_code: selected.postal_code,
        city: selected.place_name,
        street,
        description: primaryDescription,
        latitude: coord.lat,
        longitude: coord.lng,
      });
      const { data: inserted, error } = await supabase.from("businesses").insert({
        name: payload.name,
        kind: payload.kind,
        country: payload.country,
        city: payload.city,
        region: selected.admin_name1 || null,
        postal_code: payload.postal_code,
        street: payload.street || null,
        description: payload.description || null,
        descriptions: cleanedDescriptions,
        latitude: payload.latitude,
        longitude: payload.longitude,
        owner_id: user.id,
      } as never).select("id").single();
      if (error) throw error;
      const businessId = inserted.id as string;

      // Upload photos sequentially; collect failures but don't block business creation
      const failures: string[] = [];
      for (const file of photos) {
        try {
          const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
          const path = `${businessId}/${crypto.randomUUID()}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from("business-photos")
            .upload(path, file, { contentType: file.type, upsert: false });
          if (upErr) throw upErr;
          const { error: insErr } = await supabase.from("business_photos").insert({
            business_id: businessId,
            storage_path: path,
            size_bytes: file.size,
            mime_type: file.type,
          });
          if (insErr) {
            await supabase.storage.from("business-photos").remove([path]);
            throw insErr;
          }
        } catch (e) {
          failures.push((e as Error).message);
        }
      }
      return { businessId, failures };
    },
    onSuccess: ({ failures }) => {
      toast.success(t("reg.submitted"));
      if (failures.length) {
        toast.error(t("photos.uploadFailed", { msg: failures[0] }));
      }
      previews.forEach((u) => URL.revokeObjectURL(u));
      setName(""); setKind(""); setPostalInput(""); setSelected(null); setCoord(null); setStreet(""); setDescriptions({});
      setPhotos([]); setPreviews([]);
      navigate({ to: "/moje-podniky" });
    },
    onError: (e: Error) => {
      const msg = e instanceof z.ZodError ? e.errors[0]?.message ?? t("reg.invalidData") : e.message;
      toast.error(msg);
    },
  });

  const canSubmit = useMemo(
    () => name.trim().length >= 2 && !!kind && addressOk && !createBusiness.isPending,
    [name, kind, addressOk, createBusiness.isPending],
  );

  if (authLoading || !user) {
    return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">{t("my.loading")}</div>;
  }


  return (
    <div className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">{t("reg.back")}</Link>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{t("reg.moderation")}</Badge>
            <LanguageSwitcher />
          </div>
        </div>

        {!gatePassed ? (
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-2xl">{t("reg.gate.title")}</CardTitle>
              <CardDescription>{t("reg.gate.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={ownerConfirmed}
                  onChange={(e) => setOwnerConfirmed(e.target.checked)}
                />
                <span className="text-sm">
                  {t("reg.gate.confirmBefore")} <strong>{t("reg.gate.confirmStrong")}</strong> {t("reg.gate.confirmAfter")}
                </span>
              </label>

              <div className="space-y-2">
                <Label>{t("reg.gate.kindsLabel")}</Label>
                <div className="flex flex-wrap gap-2">
                  {KIND_OPTIONS.map((k) => {
                    const active = confirmedKinds.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => toggleConfirmedKind(k)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition ${
                          active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                        }`}
                      >
                        {active && <Check className="h-3.5 w-3.5" />}
                        {tKind(t, k)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("reg.gate.selected", { count: confirmedKinds.length })}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => navigate({ to: "/" })}>{t("reg.cancel")}</Button>
                <Button
                  disabled={!ownerConfirmed || confirmedKinds.length === 0}
                  onClick={() => { setKind(confirmedKinds[0]); setGatePassed(true); }}
                >
                  {t("reg.continue")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-2xl">{t("reg.title")}</CardTitle>
            <CardDescription>
              {t("reg.desc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="name">{t("reg.name")}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder={t("reg.namePlaceholder")} />
            </div>

            <div className="space-y-1.5">
              <Label>{t("reg.kind")}</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as BusinessKind)}>
                <SelectTrigger><SelectValue placeholder={t("reg.kindPlaceholder")} /></SelectTrigger>
                <SelectContent>
                  {confirmedKinds.map((k) => (
                    <SelectItem key={k} value={k}>{tKind(t, k)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {confirmedKinds.length === 2 && (
                <p className="text-xs text-muted-foreground">
                  {t("reg.confirmedKinds", { list: confirmedKinds.map((k) => tKind(t, k)).join(", ") })}
                </p>
              )}
            </div>


            <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
              <div className="space-y-1.5">
                <Label>{t("reg.country")}</Label>
                <Select
                  value={country}
                  onValueChange={(v) => { setCountry(v); setPostalInput(""); setSelected(null); setCoord(null); }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const dn = new Intl.DisplayNames([lang], { type: "region" });
                      return COUNTRY_CODES.map((code) => (
                        <SelectItem key={code} value={code}>{code} — {dn.of(code) ?? code}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="psc">{t("reg.psc")}</Label>
                <Input
                  id="psc"
                  value={postalInput}
                  onChange={(e) => setPostalInput(e.target.value)}
                  placeholder={t("reg.pscPlaceholder")}
                  inputMode="numeric"
                  maxLength={12}
                />
                {!postalValid && postalInput.length > 0 && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {t("reg.pscInvalid")}
                  </p>
                )}
              </div>
            </div>

            {postalValid && (
              <div className="rounded-md border bg-background p-3">
                {psfetching ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t("reg.searching")}
                  </div>
                ) : matches.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" /> {t("reg.notFound", { postal })}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">
                      {matches.length === 1 ? t("reg.foundOne") : t("reg.foundMany", { count: matches.length })}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {matches.map((m, i) => {
                        const active = selected?.place_name === m.place_name;
                        return (
                          <button
                            key={`${m.place_name}-${i}`}
                            type="button"
                            onClick={() => setSelected(m)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition ${
                              active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                            }`}
                          >
                            {active && <Check className="h-3.5 w-3.5" />}
                            {m.place_name}
                            {m.admin_name1 && <span className="opacity-70">· {m.admin_name1}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {addressOk && coord && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {t("reg.coords")} {coord.lat.toFixed(5)}, {coord.lng.toFixed(5)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {addressOk && coord && (
              <div className="space-y-1.5">
                <Label>{t("reg.mapPreview")}</Label>
                <LocationPreviewMap
                  lat={coord.lat}
                  lng={coord.lng}
                  label={selected?.place_name}
                  onChange={(lat, lng) => setCoord({ lat, lng })}
                />
                <p className="text-xs text-muted-foreground">{t("reg.mapHint")}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="street">{t("reg.street")}</Label>
              <Input id="street" value={street} onChange={(e) => setStreet(e.target.value)} maxLength={200} placeholder={t("reg.streetPlaceholder")} />
            </div>

            <div className="space-y-2">
              <Label>{t("reg.descLabel")}</Label>
              <p className="text-xs text-muted-foreground">{t("reg.descMultiHint")}</p>
              <div className="space-y-2">
                {LANGS.map(({ code, label }) => (
                  <div key={code} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded bg-brand-earth/10 px-1.5 text-[10px] font-bold uppercase text-brand-earth">
                        {label}
                      </span>
                      <Label htmlFor={`desc-${code}`} className="text-xs text-muted-foreground">
                        {t(`lang.${code}`)}
                      </Label>
                    </div>
                    <Textarea
                      id={`desc-${code}`}
                      value={descriptions[code] ?? ""}
                      onChange={(e) =>
                        setDescriptions((prev) => ({ ...prev, [code]: e.target.value }))
                      }
                      maxLength={1000}
                      rows={3}
                      placeholder={t("reg.descPlaceholder")}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("photos.title")}</Label>
              <div className="flex flex-wrap gap-2">
                {previews.map((src, i) => (
                  <div key={i} className="group relative h-20 w-20 overflow-hidden rounded-md border bg-muted">
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute right-0.5 top-0.5 rounded bg-background/80 p-0.5 opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-md border border-dashed text-muted-foreground hover:bg-accent">
                  <ImagePlus className="h-5 w-5" />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
                  />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">{t("photos.maxSize", { price: "—" })}</p>
            </div>


            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => navigate({ to: "/" })}>{t("reg.cancel")}</Button>
              <Button onClick={() => createBusiness.mutate()} disabled={!canSubmit}>
                {createBusiness.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("reg.submit")}
              </Button>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
